import { memoize } from '@adv-frontend/modules-memo';
import type { Flow } from '../../contract/flow';
import type { AnyGetter, GetterFactoryArgs, GetterParam, HasGetterParam } from '../lib/getterParam';
import { ComputedFlow } from './instance';
import type { FlowComputationContext } from './computation';

/**
 * Функция, которая вычисляет значение потока и не требует параметров.
 *
 * @typeParam Data - Тип значения, возвращаемого вычислением
 * @param ctx - Контекст вычисления
 * @returns Вычисленное значение
 */
export type ComputedFlowGetter<Data> = (ctx: FlowComputationContext) => Data;

/**
 * Функция, которая вычисляет значение потока и требует параметр.
 *
 * @typeParam Data - Тип значения, возвращаемого вычислением
 * @typeParam Param - Тип параметра, передаваемого в вычисление
 * @param ctx - Контекст вычисления
 * @param param - Значение параметра, используемое в вычислении
 * @returns Вычисленное значение
 */
export type ComputedFlowGetterWithParam<Data, Param> = (ctx: FlowComputationContext, param: Param) => Data;

/**
 * Опции конфигурации для вычисляемых потоков.
 *
 * @typeParam Data - Тип значения, возвращаемого вычислением
 * @typeParam Param - Тип параметра, передаваемого в вычисление (если есть)
 */
export interface ComputedFlowOptions<Data, Param> {
    /**
     * Начальное значение для использования, когда вычисление завершается с ошибкой прерывания
     * и нет доступного кешированного значения.
     */
    initialValue?: Data;

    /**
     * Функция для сравнения вычисленных значений, чтобы определить, изменились ли они.
     *
     * Используется для предотвращения ненужных обновлений, когда вычисленное значение
     * эквивалентно предыдущему значению, даже если они не строго равны.
     *
     * @param a - Предыдущее вычисленное значение
     * @param b - Новое вычисленное значение
     * @returns `true`, если значения считаются равными, `false` в противном случае
     * @default Object.is
     *
     * @example
     * ```typescript
     * // Пользовательское равенство для объектов
     * equals: (a, b) => deepEqual(a, b)
     * ```
     */
    equals?: (a: Data, b: Data) => boolean;

    /**
     * Функция для сравнения параметров, чтобы определить равенство ключей кеша.
     *
     * Используется для поиска существующих вычисляемых потоков в кеше, когда
     * один и тот же параметр используется несколько раз.
     *
     * @param a - Первый параметр для сравнения
     * @param b - Второй параметр для сравнения
     * @returns `true`, если параметры считаются равными, `false` в противном случае
     * @default Object.is
     *
     * @example
     * ```typescript
     * // Пользовательское сравнение объектных параметров
     * paramEquals: (a, b) => a.id === b.id
     * ```
     */
    paramEquals?: (a: Param, b: Param) => boolean;
}

/**
 * Тип значения вычисляемого потока.
 *
 * Если тип данных не задан явно, он выводится из возвращаемого значения геттера.
 *
 * @typeParam Data - Явно указанный тип значения
 * @typeParam Getter - Функция вычисления
 */
export type ComputedFlowData<Data, Getter extends AnyGetter> = unknown extends Data ? ReturnType<Getter> : Data;

/**
 * Результат создания вычисляемого потока.
 *
 * Для геттера со вторым параметром это фабрика потоков, иначе — один поток. Для поддерживаемых форм
 * геттера условие совпадает с рантайм-проверкой арности.
 *
 * @typeParam Data - Явно указанный тип значения
 * @typeParam Param - Явно указанный тип параметра
 * @typeParam Getter - Функция вычисления
 */
export type ComputedFlowResult<Data, Param, Getter extends AnyGetter> = Getter extends AnyGetter
    ? HasGetterParam<Getter> extends true
        ? (...args: GetterFactoryArgs<Getter, Param>) => Flow<ComputedFlowData<Data, Getter>>
        : Flow<ComputedFlowData<Data, Getter>>
    : never;

/**
 * Создает вычисляемый поток.
 *
 * Если у геттера объявлен второй параметр, возвращается фабрика потоков,
 * по одному на каждое значение параметра, иначе — один поток. В рантайме вариант выбирается
 * по `getter.length > 1`, в типах — по списку параметров геттера.
 *
 * Значение по умолчанию у параметра задавать нельзя: у `(ctx, id = 'x') => ...` арность равна 1,
 * а в типах такой параметр неотличим от необязательного, и типы посчитают геттер
 * параметризованным. То же самое с параметром через rest-кортеж `(ctx, ...args: [id: string]) => ...`
 * и с перегруженным геттером, у которого типы видят только последнюю сигнатуру.
 *
 * Типовые аргументы указывают только для параметризованного варианта и сразу оба: явный типовой
 * аргумент отключает вывод типа геттера, и вариант без параметра выбрать уже нельзя. Тип значения
 * потока без параметра задают аннотацией возвращаемого значения геттера.
 *
 * @typeParam Data - Тип значения, возвращаемого вычислением
 * @typeParam Param - Тип параметра, передаваемого в вычисление (если есть)
 * @typeParam Getter - Функция вычисления, выводится из аргумента
 * @param getter - Функция, которая вычисляет значение
 * @param options - Опциональная конфигурация для вычисляемого потока
 * @returns Экземпляр вычисляемого потока либо фабрика таких экземпляров
 *
 * @example
 * ```typescript
 * const counter = computedFlow((ctx) => {
 *   return ctx.watch(numberFlow) * 2;
 * });
 * ```
 *
 * @example
 * ```typescript
 * const userProfile = computedFlow((ctx, userId: string) => {
 *   return ctx.watch(userFlow(userId));
 * });
 *
 * const johnProfile = userProfile('john123');
 * const janeProfile = userProfile('jane456');
 * ```
 */
export function computedFlow<
    Data = unknown,
    Param = never,
    Getter extends ComputedFlowGetterWithParam<Data, Param> = ComputedFlowGetterWithParam<Data, Param>,
>(
    getter: Getter,
    options?: ComputedFlowOptions<ComputedFlowData<Data, Getter>, GetterParam<Getter, Param>>
): ComputedFlowResult<Data, Param, Getter>;

export function computedFlow<Data = unknown, Param = never>(
    getter: ComputedFlowGetterWithParam<Data, Param>,
    options?: ComputedFlowOptions<Data, Param>
): Flow<Data> | ((param: Param) => Flow<Data>) {
    if (isGetterWithoutParam(getter)) {
        // Создаем единственный вычисляемый поток для непараметризованных геттеров
        return withContext(() => new ComputedFlow<Data>(ctx => getter(ctx), options));
    }

    // Создаем мемоизированную фабрику для параметризованных вычисляемых потоков
    return memoize(
        (param: Param) => {
            return new ComputedFlow<Data>(ctx => {
                return getter(ctx, param);
            }, options);
        },
        {
            equals: options?.paramEquals,
        }
    );
}

/**
 * Страж типа для определения, принимает ли функция-геттер только контекст.
 *
 * Проверяет арность (количество параметров) функции-геттера. Тип результата фабрики
 * выводится по тому же признаку — по наличию обязательного второго параметра у геттера.
 *
 * @typeParam Data - Тип значения, возвращаемого вычислением
 * @typeParam Param - Тип параметра, передаваемого в вычисление
 * @param getter - Функция-геттер для проверки
 * @returns `true`, если геттер не принимает параметров помимо контекста, `false` в противном случае
 */
function isGetterWithoutParam<Data, Param>(
    getter: ComputedFlowGetterWithParam<Data, Param>
): getter is (ctx: FlowComputationContext) => Data {
    return getter.length <= 1;
}

type LazyFlowFactory<T> = () => Flow<T>;

/**
 * Создает контекстно-зависимый прокси потока, который разделяет экземпляры потоков между запросами.
 * Это обеспечивает правильную поддержку SSR, поддерживая изолированные экземпляры потоков для каждого контекста выполнения.
 *
 * @typeParam T - Тип значения, которое производит поток
 * @param createFlow - Фабричная функция, которая создает фактический экземпляр потока
 * @returns Прокси потока, который управляет контекстно-специфичными экземплярами
 */
function withContext<T>(createFlow: LazyFlowFactory<T>): Flow<T> {
    const getFlow = memoize(createFlow);

    return {
        subscribe(listener) {
            return getFlow().subscribe(listener);
        },
        getSnapshot() {
            return getFlow().getSnapshot();
        },
        getCachedSnapshot() {
            return getFlow().getCachedSnapshot();
        },
    };
}
